<template>
  <div class="border border-gray-200 rounded-lg p-4 py-2 mb-4">
    <div class="grid grid-cols-1 sm:grid-cols-6 gap-4 sm:gap-6 items-center">
      <div class="flex items-start gap-4 col-span-1">
        <div class="flex flex-col text-sm text-start">
          <span class="text-textPrimary text-md mb-1 font-bold">Data do plantão</span>
          <span>
            <strong class="text-textPrimary">#{{ exceptionId }}</strong>
            · {{ formattedDate }}
          </span>
        </div>

        <div class="h-12 self-center border-l border-gray-200 hidden sm:block"></div>
      </div>

      <div class="flex flex-col text-sm text-start col-span-1">
        <span class="text-textPrimary font-bold text-md mb-1">Status</span>
        <StatusBadge :status="status" />
      </div>

      <div class="flex flex-col text-sm text-start col-span-1">
        <span class="text-textPrimary font-bold text-md mb-1">Médico</span>
        <span>
          {{ doctorName }}
          <strong v-if="doctorCrm" class="text-textPrimary"> {{ doctorCrm }} </strong>
        </span>
      </div>

      <div class="flex flex-col text-sm text-start col-span-1">
        <span class="text-textPrimary font-bold text-md mb-1 truncate block max-w-full"
          >Escala</span
        >

        <span>
          {{ scale || 'Não informada' }} <br />

          <small class="text-textPrimary truncate block max-w-full">{{
            unit || 'Não informada'
          }}</small>
        </span>
      </div>

      <div class="flex flex-col text-sm text-start col-span-1">
        <span class="text-textPrimary font-bold text-md mb-1">Prejuízo previsto</span>
        <span class="text-red-600">{{ formattedPrejudice }}</span>
      </div>

      <div class="col-span-1 flex items-end justify-start sm:justify-end w-full">
        <OutlinedButton
          text="Ver detalhes"
          width="100%"
          height="32px"
          class="sm:!w-[160px]"
          @click="$emit('details', props.exceptionId)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import {
  Doctor,
  ExceptionStatusEnum
} from '@/Network/Types/Requests/DoctorStationExceptionRequests'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { getFirstAndSecondName } from '@/utils/getFirstAndSecondName'
import StatusBadge from '@/components/designSystem/Badges/StatusBadge.vue'

const props = defineProps({
  date: {
    type: String,
    default: ''
  },
  status: {
    type: String as () => ExceptionStatusEnum,
    required: true,
    validator: (val: ExceptionStatusEnum) => Object.values(ExceptionStatusEnum).includes(val)
  },
  doctor: {
    type: Object as () => Doctor | null,
    default: null
  },
  scale: {
    type: String,
    default: ''
  },
  unit: {
    type: String,
    default: ''
  },
  prejudice: {
    type: String,
    required: true
  },
  exceptionId: {
    type: [Number],
    required: true
  }
})

defineEmits<{
  (e: 'details', exceptionId: number): void
}>()

const formattedDate = computed(() => {
  if (props.date) {
    try {
      return format(new Date(props.date), 'dd/MM/yyyy', { locale: ptBR })
    } catch (e) {
      console.error('Error formatting date:', e)
      return props.date
    }
  }
  return 'Data não informada'
})

const formattedPrejudice = computed(() => {
  if (props.prejudice) {
    const numericValue = parseFloat(props.prejudice)
    if (!isNaN(numericValue)) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(numericValue)
    }
  }
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(0)
})

const doctorName = computed(() => {
  if (props.doctor && props.doctor.name) {
    return getFirstAndSecondName(props.doctor.name)
  }
  return 'Médico não informado'
})

const doctorCrm = computed(() => {
  if (props.doctor && props.doctor.crm && props.doctor.crm.number && props.doctor.crm.uf) {
    return `${props.doctor.crm.number}-${props.doctor.crm.uf}`
  }
  return ''
})
</script>
